import React, {useState} from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useGetNumberingSortingView} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const DemoNumberingSortingView: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(59);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);

  const numberingSortingQuery = useGetNumberingSortingView(
    {SecurityGroupId: securityGroupId, productionId: productionId},
    isEnabled
  );

  const handleFetch = () => {
    setIsEnabled(true);
  };

  const handleReset = () => {
    setIsEnabled(false);
    setSecurityGroupId(1);
    setProductionId(59);
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🔢 Numbering & Sorting View API Demo
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Test the numbering and sorting view endpoint for production
          configuration
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Numbering & Sorting View
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/view/numberingsortingview
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get numbering and sorting configuration including sort order
            options, tags list, and production settings
          </Typography>
        </CardContent>
      </Card>
      {/* Parameters Section */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Request Parameters
          </Typography>

          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
              sx={{minWidth: 150}}
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
              sx={{minWidth: 150}}
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={numberingSortingQuery.isLoading}
              data-testid="demo-numbering-sorting-view-button">
              {numberingSortingQuery.isLoading ? 'Loading...' : 'Fetch Data'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={numberingSortingQuery.isLoading}
              data-testid="demo-numbering-sorting-view-reset-button">
              Reset
            </Button>
          </Box>

          {numberingSortingQuery.isLoading && (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
              <CircularProgress />
            </Box>
          )}

          {numberingSortingQuery.error && (
            <Alert severity="error" sx={{marginTop: 2}}>
              Error: {numberingSortingQuery.error.message}
            </Alert>
          )}
        </CardContent>
      </Card>
      {/* Response Data */}
      {numberingSortingQuery.data && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Response Data
            </Typography>

            {/* Sort Order Options */}
            <Accordion sx={{marginBottom: 2}}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="typo-text subhead4_size">
                  Sort Order Options (
                  {numberingSortingQuery.data.sortOrder?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {numberingSortingQuery.data.sortOrder && (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Key</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Selected</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {numberingSortingQuery.data.sortOrder.map(
                          (item: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{item.key}</TableCell>
                              <TableCell>{item.value}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.isSelected ? 'Yes' : 'No'}
                                  color={
                                    item.isSelected ? 'success' : 'default'
                                  }
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Tags List */}
            <Accordion sx={{marginBottom: 2}}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="typo-text subhead4_size">
                  Tags List ({numberingSortingQuery.data.lsttags?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {numberingSortingQuery.data.lsttags && (
                  <TableContainer component={Paper} sx={{maxHeight: 400}}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Text</TableCell>
                          <TableCell>Parent</TableCell>
                          <TableCell>Is Group</TableCell>
                          <TableCell>Selected</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {numberingSortingQuery.data.lsttags.map(
                          (tag: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{tag.id}</TableCell>
                              <TableCell>{tag.text}</TableCell>
                              <TableCell>{tag.parent}</TableCell>
                              <TableCell>
                                <Chip
                                  label={tag.isGroup ? 'Yes' : 'No'}
                                  color={tag.isGroup ? 'primary' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={tag.isSelected ? 'Yes' : 'No'}
                                  color={tag.isSelected ? 'success' : 'default'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Production Status */}
            <Accordion sx={{marginBottom: 2}}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="typo-text subhead4_size">
                  Production Status
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <Typography className="typo-text body3_size">
                    Status:
                  </Typography>
                  <Chip
                    label={numberingSortingQuery.data.productionStatusCode}
                    color="info"
                    size="small"
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Production Settings */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="typo-text subhead4_size">
                  Production Settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    backgroundColor: '#f5f5f5',
                    padding: 2,
                    borderRadius: 1,
                  }}
                >
                  <pre>
                    {JSON.stringify(
                      numberingSortingQuery.data
                        .productionNumberingAndSortingModel,
                      null,
                      2
                    )}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}
      {/* API Details */}
      <Card sx={{marginTop: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            API Details
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Request Parameters:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              SecurityGroupId: number (required)
              <br />
              productionId: number (required)
            </Box>
          </Box>

          <Box>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Response Format:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {`{
  "sortOrder": [...],
  "sortKeepDocsWithNoMasterDate": [...],
  "lsttags": [...],
  "productionStatusCode": "string",
  "productionNumberingAndSortingModel": {...}
}`}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DemoNumberingSortingView;
